import React, { useEffect, useRef, useState } from 'react';
import { UX2 } from '@wsb/guac-widget-core';
import PropTypes from 'prop-types';
import {
  CartApi,
  CartConstants,
  saveOrderCookie
} from '@wsb/guac-widget-shared/lib/common/ols-core/shop-bundle';
import { CartActions } from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import { AppDispatcher } from '@wsb/guac-widget-shared/lib/common/ols-core/shared-bundle';
import dataAids from '../../constants/dataAids';
import { CookieBannerUtils } from '../utils/ols-core';
import { checkoutUrl } from './utils/CheckoutUtils';
import { clickIdTracker } from '../utils/TrackingUtils';
import * as Metrics from './utils/trackMetric';
import * as MetricEvents from '../../constants/metrics';
import { DigitalWalletConstants } from '../utils/ols-core/DigitalWalletStore';

const { dispatchViewAction } = AppDispatcher;

export function BuyNowButton({
  isEditMode,
  state,
  disabled,
  getOrderData,
  setHasAttemptedPurchase,
  translate,
  isGalleryDetail,
  experimentEnabled,
  productId,
  isQuickView,
  isLowInventoryInGalleryEnabled,
  isLowInventoryInGallery,
  metricTrackRef = {}
}) {
  const { Button } = UX2.Element;
  const nonceRef = useRef(null);
  const buyNowText = translate('products.buy_now');
  let order;
  const view = isGalleryDetail ? 'prodlist' : 'pdp';
  const [impressionWasTracked, setImpressionWasTracked] = useState(false);

  useEffect(() => {
    if (!impressionWasTracked && !metricTrackRef?.current) {
      if (isQuickView && isLowInventoryInGalleryEnabled && isLowInventoryInGallery) {
        Metrics.track(MetricEvents.QUICK_VIEW_MODAL_LOW_IN_STOCK_BUY_NOW_BUTTON_IMPRESSION);
      } else if (isQuickView && isLowInventoryInGalleryEnabled) {
        Metrics.track(MetricEvents.QUICK_VIEW_MODAL_BUY_NOW_BUTTON_IMPRESSION);
      } else if (experimentEnabled) {
        Metrics.track(MetricEvents.BUY_NOW_BUTTON_LOW_INVENTORY_IMPRESSION(view));
      } else {
        Metrics.track(MetricEvents.BUY_NOW_BUTTON_IMPRESSION(view));
      }
      setImpressionWasTracked(true);
    }
  }, [experimentEnabled, isQuickView, isLowInventoryInGalleryEnabled, isLowInventoryInGallery]);

  useEffect(() => {
    if (metricTrackRef?.current === null) {
      metricTrackRef.current = true;
    }
  }, []);

  function setNonce() {
    const input = nonceRef.current;
    input.value = order.nonce;
  }

  async function createBuyNowOrder({ id, quantity, options }) {
    order = await CartApi.createCart();
    await CartApi.addToCart(order, id, quantity, options);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (isEditMode) return;

    const { variant } = getOrderData();

    if (!variant?.canCheckout) {
      setHasAttemptedPurchase();
      return;
    }

    if (isQuickView && isLowInventoryInGalleryEnabled && isLowInventoryInGallery) {
      Metrics.track(MetricEvents.QUICK_VIEW_MODAL_LOW_IN_STOCK_BUY_NOW_BUTTON_CLICK);
    } else if (isQuickView && isLowInventoryInGalleryEnabled) {
      Metrics.track(MetricEvents.QUICK_VIEW_MODAL_BUY_NOW_BUTTON_CLICK);
    } else if (experimentEnabled) {
      Metrics.track(MetricEvents.BUY_NOW_BUTTON_LOW_INVENTORY_CLICK(view));
    } else {
      Metrics.track(MetricEvents.BUY_NOW_BUTTON_CLICK(view));
    }

    dispatchViewAction({
      type: DigitalWalletConstants.SESSION_STARTED
    });

    try {
      await createBuyNowOrder(variant?.values);
      setNonce();
      saveOrderCookie({
        number: order.number,
        token: order.token
      });
      CartActions.doCheckout(null, productId);
    } catch (error) {
      Metrics.track(MetricEvents.BUY_NOW_BUTTON_ERROR(view));
      dispatchViewAction({
        type: DigitalWalletConstants.SESSION_FAILED
      });

      dispatchViewAction({
        type: CartConstants.ERROR_CART_ITEM_ADD,
        error: { messages: error?.response?.errors }
      });
    }
  }

  return (
    <form
      id={ productId ? `do_checkout_form_${productId}` : dataAids.DO_CHECKOUT_ID }
      action={ checkoutUrl() }
      method='POST'
      onSubmit={ handleSubmit }
      style={{ width: '100%' }}
    >
      <input type='hidden' name='cookie_terms_accepted' value={ CookieBannerUtils.isAccepted } />
      <input type='hidden' name='order_nonce' ref={ nonceRef } />
      <input type='hidden' name='gdan_clid' value={ clickIdTracker.getGdanClidValue() } />
      <input type='hidden' name='gclid' value={ clickIdTracker.getGoogleClickId() } />
      <input type='hidden' name='_fbc' value={ clickIdTracker.getFacebookClickId() } />
      <input type='hidden' name='_fbp' value={ clickIdTracker.getFacebookBrowserId() } />

      <Button.Primary
        id='buynow-btn'
        type='submit'
        state={ state }
        disabled={ disabled }
        data-aid={ dataAids.BUY_NOW }
        style={{ width: '100% !important' }}
      >
        { buyNowText }
      </Button.Primary>
    </form>
  );
}

BuyNowButton.propTypes = {
  disabled: PropTypes.bool,
  getOrderData: PropTypes.func,
  isEditMode: PropTypes.bool,
  setHasAttemptedPurchase: PropTypes.func,
  state: PropTypes.string,
  translate: PropTypes.func.isRequired,
  isGalleryDetail: PropTypes.bool,
  experimentEnabled: PropTypes.bool,
  productId: PropTypes.string,
  isQuickView: PropTypes.bool,
  isLowInventoryInGalleryEnabled: PropTypes.bool,
  isLowInventoryInGallery: PropTypes.bool,
  metricTrackRef: PropTypes.object
};
